import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

import { authInfoQueryKey } from "../auth/AuthProvider";
import type { AuthSession } from "../auth/auth-context";
import { rpc, type Policy } from "./rpc";
import type {
  ApiKey,
  AuthConfig,
  Collection,
  FilterCondition,
  Group,
  GroupMembership,
  GroupPermission,
  InviteEntry,
  InviteResult,
  NotificationSettings,
  Team,
  User,
  UserPreferences,
  UserRole,
} from "./types";

export const queryKeys = {
  authConfig: ["auth.config"] as const,
  collections: ["collections.list"] as const,
  apiKeys: ["apiKeys.list"] as const,
  users: (params: UsersListParams) => ["users.list", params] as const,
  groups: (query: string) => ["groups.list", query] as const,
  group: (id: string) => ["groups.info", id] as const,
  groupMembers: (id: string) => ["groups.memberships", id] as const,
};

export function useAuthConfig(): UseQueryResult<AuthConfig> {
  return useQuery({
    queryKey: queryKeys.authConfig,
    queryFn: async () => (await rpc<AuthConfig>("auth.config")).data,
    retry: false,
  });
}

export interface CollectionListResult {
  collections: Collection[];
  /** Per-collection abilities, indexed by id, from the envelope's `policies`. */
  abilities: Record<string, Record<string, boolean>>;
}

async function fetchCollections(): Promise<CollectionListResult> {
  const result = await rpc<Collection[]>("collections.list", { limit: 100 });
  return { collections: result.data, abilities: indexAbilities(result.policies) };
}

/**
 * One `collections.list` query serves both readers. The sidebar needs the
 * records; its row menus need the abilities that came with them, and a second
 * hook over the same key with a different `select` is how both get what they
 * want from a single request.
 */
export function useCollections(enabled = true): UseQueryResult<Collection[]> {
  return useQuery({
    queryKey: queryKeys.collections,
    queryFn: fetchCollections,
    select: (result: CollectionListResult) => result.collections,
    enabled,
    retry: false,
  });
}

export function useCollectionAbilities(
  enabled = true,
): UseQueryResult<Record<string, Record<string, boolean>>> {
  return useQuery({
    queryKey: queryKeys.collections,
    queryFn: fetchCollections,
    select: (result: CollectionListResult) => result.abilities,
    enabled,
    retry: false,
  });
}

/** Path for a collection row; `url` is the server's canonical slug path. */
export function collectionPath(collection: Collection): string {
  return collection.url ?? `/collection/${collection.id}`;
}

export function useApiKeys(): UseQueryResult<ApiKey[]> {
  return useQuery({
    queryKey: queryKeys.apiKeys,
    queryFn: async () => (await rpc<ApiKey[]>("apiKeys.list", { limit: 100 })).data,
    retry: false,
  });
}

export function useCreateApiKey(): UseMutationResult<ApiKey, Error, { name: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string }) =>
      (await rpc<ApiKey>("apiKeys.create", input)).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys });
    },
  });
}

export function useDeleteApiKey(): UseMutationResult<void, Error, { id: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      await rpc("apiKeys.delete", input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys });
    },
  });
}

export function useUpdateUser(): UseMutationResult<User, Error, { id: string; name: string }> {
  return useMutation({
    mutationFn: async (input: { id: string; name: string }) =>
      (await rpc<User>("users.update", input)).data,
  });
}

export function useCreateCollection(): UseMutationResult<Collection, Error, { name: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string }) =>
      (await rpc<Collection>("collections.create", input)).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.collections });
    },
  });
}

/* ------------------------------------------------------------------------- *
 * Settings: workspace, people, and groups
 * ------------------------------------------------------------------------- */

/** Parameters `teams.update` accepts (`docs/spec/users-and-teams.md` § API surface). */
export type TeamUpdateInput = Partial<
  Pick<
    Team,
    | "name"
    | "description"
    | "avatarUrl"
    | "subdomain"
    | "sharing"
    | "guestSignin"
    | "documentEmbeds"
    | "memberCollectionCreate"
    | "memberTeamCreate"
    | "defaultCollectionId"
    | "defaultUserRole"
    | "inviteRequired"
    | "allowedDomains"
    | "preferences"
  >
>;

/** Parameters `users.update` accepts. `id` defaults to the caller server-side. */
export type UserUpdateInput = {
  id?: string;
} & Partial<Pick<User, "name" | "avatarUrl" | "language" | "timezone">> & {
    preferences?: UserPreferences;
    notificationSettings?: NotificationSettings;
  };

function patchSession(
  current: AuthSession | null | undefined,
  patch: (session: AuthSession) => AuthSession,
): AuthSession | null {
  if (current === null || current === undefined) return null;
  return patch(current);
}

/**
 * `teams.update` with an optimistic write into the `auth.info` cache: the
 * workspace record the whole client reads lives there, so a toggle must move at
 * once and snap back if the server refuses. The snapshot taken in `onMutate` is
 * the rollback, and `onSettled` re-reads the truth either way.
 */
export function useTeamUpdate(): UseMutationResult<
  Team,
  Error,
  TeamUpdateInput,
  { previous: AuthSession | null | undefined }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: TeamUpdateInput) => (await rpc<Team>("teams.update", input)).data,
    onMutate: async (input: TeamUpdateInput) => {
      await queryClient.cancelQueries({ queryKey: authInfoQueryKey });
      const previous = queryClient.getQueryData<AuthSession | null>(authInfoQueryKey);
      queryClient.setQueryData<AuthSession | null>(authInfoQueryKey, (current) =>
        patchSession(current, (session) => ({
          ...session,
          info: {
            ...session.info,
            team: {
              ...session.info.team,
              ...input,
              ...(input.preferences === undefined
                ? {}
                : {
                    preferences: { ...session.info.team.preferences, ...input.preferences },
                  }),
            },
          },
        })),
      );
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context !== undefined) {
        queryClient.setQueryData(authInfoQueryKey, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: authInfoQueryKey });
    },
  });
}

/**
 * `users.update` for the caller's own record, optimistic in the same way. Both
 * bags are merged key-by-key by the server, so the client sends only what
 * changed — except `notificationSettings`, which the settings screen sends whole
 * (`docs/spec/notifications.md` §5).
 */
export function useSelfUpdate(): UseMutationResult<
  User,
  Error,
  UserUpdateInput,
  { previous: AuthSession | null | undefined }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UserUpdateInput) => (await rpc<User>("users.update", input)).data,
    onMutate: async (input: UserUpdateInput) => {
      await queryClient.cancelQueries({ queryKey: authInfoQueryKey });
      const previous = queryClient.getQueryData<AuthSession | null>(authInfoQueryKey);
      queryClient.setQueryData<AuthSession | null>(authInfoQueryKey, (current) =>
        patchSession(current, (session) => ({
          ...session,
          info: {
            ...session.info,
            user: {
              ...session.info.user,
              ...input,
              ...(input.preferences === undefined
                ? {}
                : { preferences: { ...session.info.user.preferences, ...input.preferences } }),
            },
          },
        })),
      );
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context !== undefined) {
        queryClient.setQueryData(authInfoQueryKey, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: authInfoQueryKey });
    },
  });
}

export interface UsersListParams {
  query?: string;
  filters?: FilterCondition[];
  limit: number;
  offset: number;
}

export interface UsersListResult {
  users: User[];
  /** Per-user abilities, indexed by user id, from the envelope's `policies`. */
  abilities: Record<string, Record<string, boolean>>;
  total: number;
}

function indexAbilities(policies: Policy[]): Record<string, Record<string, boolean>> {
  const index: Record<string, Record<string, boolean>> = {};
  for (const policy of policies) index[policy.id] = policy.abilities;
  return index;
}

/** The body `users.list` is sent, kept out of the hook so tests can reuse it. */
export function usersListBody(params: UsersListParams): Record<string, unknown> {
  const trimmed = params.query?.trim() ?? "";
  return {
    ...(trimmed === "" ? {} : { query: trimmed }),
    ...(params.filters === undefined || params.filters.length === 0
      ? {}
      : { filters: params.filters }),
    sort: "createdAt",
    direction: "DESC",
    limit: params.limit,
    offset: params.offset,
  };
}

export function useUsersList(
  params: UsersListParams,
  enabled = true,
): UseQueryResult<UsersListResult> {
  return useQuery({
    enabled,
    queryKey: queryKeys.users(params),
    queryFn: async () => {
      const result = await rpc<User[]>("users.list", usersListBody(params));
      return {
        users: result.data,
        abilities: indexAbilities(result.policies),
        total: result.pagination?.total ?? result.data.length,
      };
    },
    retry: false,
    placeholderData: (previous: UsersListResult | undefined) => previous,
  });
}

/**
 * A factory for the small `users.*` commands. Each is one method with one body
 * and the same invalidation, so they are generated rather than copied; the
 * returned value is an ordinary custom hook.
 */
function makeListMutation<TInput>(
  method: string,
  invalidate: readonly unknown[],
): () => UseMutationResult<unknown, Error, TInput> {
  return function useListMutation(): UseMutationResult<unknown, Error, TInput> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (input: TInput) => (await rpc(method, input)).data,
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: invalidate });
      },
    });
  };
}

const usersKey = ["users.list"] as const;
const groupsKey = ["groups.list"] as const;

export const useUpdateUserRole = makeListMutation<{ id: string; role: UserRole }>(
  "users.update_role",
  usersKey,
);
export const useSuspendUser = makeListMutation<{ id: string }>("users.suspend", usersKey);
export const useActivateUser = makeListMutation<{ id: string }>("users.activate", usersKey);
export const useResendInvite = makeListMutation<{ id: string }>("users.resendInvite", usersKey);
export const useDeleteUser = makeListMutation<{ id: string }>("users.delete", usersKey);

export function useInviteUsers(): UseMutationResult<InviteResult, Error, InviteEntry[]> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (invites: InviteEntry[]) =>
      (await rpc<InviteResult>("users.invite", { invites })).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: usersKey });
    },
  });
}

export interface GroupsListResult {
  groups: Group[];
  abilities: Record<string, Record<string, boolean>>;
  total: number;
}

export function useGroups(params: { query?: string } = {}): UseQueryResult<GroupsListResult> {
  const trimmed = params.query?.trim() ?? "";
  return useQuery({
    queryKey: queryKeys.groups(trimmed),
    queryFn: async () => {
      const result = await rpc<Group[]>("groups.list", {
        ...(trimmed === "" ? {} : { query: trimmed }),
        limit: 100,
      });
      return {
        groups: result.data,
        abilities: indexAbilities(result.policies),
        total: result.pagination?.total ?? result.data.length,
      };
    },
    retry: false,
  });
}

export const useCreateGroup = makeListMutation<{ name: string; description?: string }>(
  "groups.create",
  groupsKey,
);
export const useUpdateGroup = makeListMutation<{
  id: string;
  name: string;
  description?: string;
}>("groups.update", groupsKey);
export const useDeleteGroup = makeListMutation<{ id: string }>("groups.delete", groupsKey);

export function useGroup(id: string): UseQueryResult<Group> {
  return useQuery({
    queryKey: queryKeys.group(id),
    queryFn: async () => (await rpc<Group>("groups.info", { id })).data,
    retry: false,
  });
}

export interface GroupMembersResult {
  users: User[];
  memberships: GroupMembership[];
}

interface GroupMembershipsPayload {
  users?: User[];
  groupMemberships?: GroupMembership[];
}

export function useGroupMembers(id: string): UseQueryResult<GroupMembersResult> {
  return useQuery({
    queryKey: queryKeys.groupMembers(id),
    queryFn: async () => {
      const result = await rpc<GroupMembershipsPayload>("groups.memberships", { id, limit: 100 });
      return {
        users: result.data.users ?? [],
        memberships: result.data.groupMemberships ?? [],
      };
    },
    retry: false,
  });
}

/** Membership commands refresh both the roster and the list's member counts. */
function makeGroupMemberMutation<TInput>(
  method: string,
): () => UseMutationResult<unknown, Error, TInput & { id: string }> {
  return function useGroupMemberMutation(): UseMutationResult<
    unknown,
    Error,
    TInput & { id: string }
  > {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (input: TInput & { id: string }) => (await rpc(method, input)).data,
      onSuccess: (_data, input) => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.groupMembers(input.id) });
        void queryClient.invalidateQueries({ queryKey: groupsKey });
      },
    });
  };
}

export const useAddGroupUser = makeGroupMemberMutation<{
  userId: string;
  permission: GroupPermission;
}>("groups.add_user");
export const useRemoveGroupUser = makeGroupMemberMutation<{ userId: string }>("groups.remove_user");
export const useUpdateGroupUser = makeGroupMemberMutation<{
  userId: string;
  permission: GroupPermission;
}>("groups.update_user");

/** `users.requestDelete` then `users.delete {code}` — the self-delete pair (§9). */
export function useRequestAccountDelete(): UseMutationResult<unknown, Error, void> {
  return useMutation({
    mutationFn: async () => (await rpc("users.requestDelete", {})).data,
  });
}

export function useDeleteAccount(): UseMutationResult<unknown, Error, { code: string }> {
  return useMutation({
    mutationFn: async (input: { code: string }) => (await rpc("users.delete", input)).data,
  });
}
